import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import BarberHero from "@/components/booking/BarberHero";
import ServiceStep from "@/components/booking/ServiceStep";
import ProfessionalStep from "@/components/booking/ProfessionalStep";
import DateTimeStep from "@/components/booking/DateTimeStep";
import DetailsStep from "@/components/booking/DetailsStep";
import Confirmation from "@/components/booking/Confirmation";
import LookupDialog from "@/components/booking/LookupDialog";
import BottomBar from "@/components/booking/BottomBar";
import { CaretLeft, LockKey } from "@phosphor-icons/react";
import { API } from "@/lib/api";
import { useLocale } from "@/i18n";

export default function PublicBookingPage() {
  const { slug } = useParams();
  const PUB = `${API}/public/barbers/${slug}`;
  const { t, setLocale } = useLocale();
  const [config, setConfig] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [step, setStep] = useState(0);
  const [service, setService] = useState(null);
  const [professional, setProfessional] = useState(null);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [booking, setBooking] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios
      .get(PUB)
      .then((res) => {
        setConfig(res.data);
        setLocale(res.data.barber.country === "py" ? "es" : "pt");
      })
      .catch((e) => {
        if (e.response?.status === 403) setBlocked(true);
        else if (e.response?.status === 404) setNotFound(true);
        else toast.error(t("load_error"));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PUB]);

  const handleContinue = useCallback(() => {
    if (step === 0 && !service) return toast.warning(t("pick_service"));
    if (step === 1 && (!date || !time)) return toast.warning(t("pick_datetime"));
    setStep((s) => s + 1);
  }, [step, service, date, time, t]);

  const handleConfirm = useCallback(async (name, phone) => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${PUB}/bookings`, {
        client_name: name,
        client_phone: phone,
        service_id: service.id,
        date,
        time,
        professional_id: professional?.id || null,
      });
      setBooking(res.data);
      setStep(3);
      window.scrollTo({ top: 0 });
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("book_error"));
      if (e.response?.status === 409) setStep(1);
    } finally {
      setSubmitting(false);
    }
  }, [PUB, service, date, time, professional, t]);

  const resetFlow = useCallback(() => {
    setStep(0);
    setService(null);
    setProfessional(null);
    setDate(null);
    setTime(null);
    setBooking(null);
    window.scrollTo({ top: 0 });
  }, []);

  if (notFound) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-50 flex items-center justify-center px-6 text-center" data-testid="not-found-screen">
        <div>
          <h1 className="text-2xl font-bold">{t("nf_title")}</h1>
          <p className="text-sm text-zinc-500 mt-2">{t("nf_text")}</p>
        </div>
      </div>
    );
  }

  if (blocked) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-50 flex items-center justify-center px-6 text-center" data-testid="blocked-screen">
        <div>
          <LockKey size={48} weight="duotone" className="text-amber-500 mx-auto" />
          <h1 className="text-2xl font-bold mt-4">{t("blocked_title")}</h1>
          <p className="text-sm text-zinc-500 mt-2">{t("blocked_text")}</p>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center" data-testid="loading-screen">
        <div className="h-10 w-10 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50">
      <div className="w-full max-w-md mx-auto pb-32">
        <BarberHero barber={config.barber} />

        {step > 0 && step < 3 && (
          <button
            data-testid="back-btn"
            onClick={() => setStep((s) => s - 1)}
            className="flex items-center gap-1 text-sm text-zinc-400 hover:text-amber-500 transition-colors px-4 mt-4"
          >
            <CaretLeft size={16} weight="bold" /> {t("back")}
          </button>
        )}

        <main className="px-4 mt-6 space-y-10">
          {step === 0 && <ServiceStep services={config.services} selected={service} onSelect={setService} />}

          {step === 1 && (
            <>
              {config.professionals?.length > 0 && (
                <ProfessionalStep
                  professionals={config.professionals}
                  selected={professional}
                  onSelect={(p) => {
                    setProfessional(p);
                    setTime(null);
                  }}
                />
              )}
              <DateTimeStep
                apiBase={PUB}
                service={service}
                schedule={config.barber.schedule}
                maxDaysAhead={config.max_days_ahead}
                professionalId={professional?.id || null}
                date={date}
                time={time}
                onDateChange={(d) => {
                  setDate(d);
                  setTime(null);
                }}
                onTimeChange={setTime}
              />
            </>
          )}

          {step === 2 && (
            <DetailsStep service={service} date={date} time={time} submitting={submitting} onSubmit={handleConfirm} professional={professional} />
          )}

          {step === 3 && booking && (
            <Confirmation booking={booking} barber={config.barber} onNewBooking={resetFlow} />
          )}

          {step < 3 && <LookupDialog apiBase={PUB} />}

          <p className="text-center text-xs text-zinc-600 pb-8">
            {t("page_by")} <span className="text-amber-500/80 font-semibold">Barber Menu</span>
          </p>
        </main>
      </div>

      {step < 3 && (
        <BottomBar
          service={service}
          step={step}
          submitting={submitting}
          onContinue={step === 2 ? null : handleContinue}
          formId="booking-details-form"
        />
      )}
    </div>
  );
}
